(function () {
  const STORAGE_KEY = 'tsh-cart-upsell-undo-v2';
  const UNDO_LIFETIME = 5 * 60 * 1000;
  const MAX_UNDO_STEPS = 3;
  const SOURCE = 'tsh-cart-upsells';

  function route(name, fallback) {
    return window.routes && window.routes[name]
      ? window.routes[name]
      : fallback;
  }

  function jsonHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  async function requestJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(body),
    });

    const data = await response
      .json()
      .catch(() => ({}));

    if (!response.ok || data.errors) {
      const message =
        data.description ||
        data.message ||
        data.errors ||
        'Unable to update your cart.';

      throw new Error(
        typeof message === 'string'
          ? message
          : 'Unable to update your cart.'
      );
    }

    return data;
  }

  async function getCart() {
    const response = await fetch(
      `${route('cart_url', '/cart')}.js`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to read your cart.'
      );
    }

    return response.json();
  }

  function itemHandle(item) {
    return (
      item.handle ||
      item.product_handle ||
      ''
    );
  }

  function isNested(item) {
    return Boolean(
      item.parent_relationship &&
      item.parent_relationship.parent
    );
  }

  function topLevelItems(cart) {
    return (cart.items || []).filter(
      (item) => !isNested(item)
    );
  }

  function findTopLevelByHandle(
    cart,
    handle
  ) {
    return (
      topLevelItems(cart).find(
        (item) =>
          itemHandle(item) === handle
      ) || null
    );
  }

  function normalizeProperties(
    properties
  ) {
    if (
      !properties ||
      typeof properties !== 'object'
    ) {
      return {};
    }

    return Object.keys(properties)
      .sort()
      .reduce((result, key) => {
        result[key] = properties[key];
        return result;
      }, {});
  }

  function sellingPlanId(item) {
    return (
      item?.selling_plan_allocation
        ?.selling_plan?.id || null
    );
  }

  function sameSnapshotLine(
    item,
    snapshot
  ) {
    if (!item || isNested(item)) {
      return false;
    }

    if (
      Number(item.variant_id) !==
      Number(snapshot.variantId)
    ) {
      return false;
    }

    if (
      String(
        sellingPlanId(item) || ''
      ) !==
      String(
        snapshot.sellingPlanId || ''
      )
    ) {
      return false;
    }

    return (
      JSON.stringify(
        normalizeProperties(
          item.properties
        )
      ) ===
      snapshot.propertiesSignature
    );
  }

  function snapshotLine(
    item,
    consumedQuantity
  ) {
    const properties =
      normalizeProperties(
        item.properties
      );

    return {
      handle: itemHandle(item),
      variantId: Number(
        item.variant_id
      ),
      beforeQty: Number(
        item.quantity
      ),
      consumedQty: Number(
        consumedQuantity
      ),
      properties,
      propertiesSignature:
        JSON.stringify(properties),
      sellingPlanId:
        sellingPlanId(item),
    };
  }

  function getSectionConfig() {
    const sections = [];

    if (
      document.querySelector(
        'cart-drawer'
      )
    ) {
      sections.push({
        requestId: 'cart-drawer',
        type: 'drawer',
      });
    }

    if (
      document.getElementById(
        'cart-icon-bubble'
      )
    ) {
      sections.push({
        requestId:
          'cart-icon-bubble',
        type: 'icon',
      });
    }

    const mainItems =
      document.getElementById(
        'main-cart-items'
      );

    if (mainItems?.dataset.id) {
      sections.push({
        requestId:
          mainItems.dataset.id,
        type: 'main-items',
      });
    }

    const mainFooter =
      document.getElementById(
        'main-cart-footer'
      );

    if (mainFooter?.dataset.id) {
      sections.push({
        requestId:
          mainFooter.dataset.id,
        type: 'main-footer',
      });
    }

    const seen = new Set();

    return sections
      .filter((section) => {
        if (
          !section.requestId ||
          seen.has(
            section.requestId
          )
        ) {
          return false;
        }

        seen.add(
          section.requestId
        );

        return true;
      })
      .slice(0, 5);
  }

  function sectionsUrl() {
    if (
      document.getElementById(
        'main-cart-items'
      )
    ) {
      return route(
        'cart_url',
        '/cart'
      );
    }

    const current =
      `${window.location.pathname}${window.location.search || ''}`;

    return current.startsWith('/')
      ? current
      : '/';
  }

  function mutationSectionPayload() {
    const config =
      getSectionConfig();

    return {
      config,
      sections: config.map(
        (section) =>
          section.requestId
      ),
      sections_url:
        sectionsUrl(),
    };
  }

  function parseHtml(html) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      );
  }

  function applySections(
    sections,
    config
  ) {
    if (!sections) {
      return;
    }

    config.forEach(
      (section) => {
        const html =
          sections[
            section.requestId
          ];

        if (!html) {
          return;
        }

        const parsed =
          parseHtml(html);

        if (
          section.type ===
          'drawer'
        ) {
          const target =
            document.querySelector(
              'cart-drawer .drawer__inner'
            );

          const source =
            parsed.querySelector(
              '.drawer__inner'
            );

          if (
            target &&
            source
          ) {
            target.innerHTML =
              source.innerHTML;
          }

          return;
        }

        if (
          section.type ===
          'icon'
        ) {
          const target =
            document.getElementById(
              'cart-icon-bubble'
            );

          const source =
            parsed.getElementById(
              'cart-icon-bubble'
            );

          if (
            target &&
            source
          ) {
            target.innerHTML =
              source.innerHTML;
          }

          return;
        }

        if (
          section.type ===
          'main-items'
        ) {
          const target =
            document.querySelector(
              '#main-cart-items .js-contents'
            );

          const source =
            parsed.querySelector(
              '#main-cart-items .js-contents'
            ) ||
            parsed.querySelector(
              '.js-contents'
            );

          if (
            target &&
            source
          ) {
            target.innerHTML =
              source.innerHTML;
          }

          return;
        }

        if (
          section.type ===
          'main-footer'
        ) {
          const target =
            document.querySelector(
              '#main-cart-footer .tsh-cart-summary__refresh'
            );

          const source =
            parsed.querySelector(
              '#main-cart-footer .tsh-cart-summary__refresh'
            ) ||
            parsed.querySelector(
              '.tsh-cart-summary__refresh'
            );

          if (
            target &&
            source
          ) {
            target.innerHTML =
              source.innerHTML;
          }
        }
      }
    );

    window.TSHCartUpsells
      ?.init();
  }

  function buildSectionUrl(
    sectionId
  ) {
    const url = new URL(
      route(
        'cart_url',
        '/cart'
      ),
      window.location.origin
    );

    const currentUrl =
      new URL(
        window.location.href
      );

    currentUrl.searchParams.forEach(
      (value, key) => {
        if (
          key === 'section_id' ||
          key === 'sections'
        ) {
          return;
        }

        url.searchParams.append(
          key,
          value
        );
      }
    );

    url.searchParams.set(
      'section_id',
      sectionId
    );

    return url.toString();
  }

  async function fetchSectionHtml(
    sectionId
  ) {
    const response = await fetch(
      buildSectionUrl(sectionId),
      {
        headers: {
          Accept: 'text/html',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to refresh ${sectionId}.`
      );
    }

    return response.text();
  }

  async function refreshDrawerSurface() {
    const drawer =
      document.querySelector(
        'cart-drawer'
      );

    if (!drawer) {
      return;
    }

    const html =
      await fetchSectionHtml(
        'cart-drawer'
      );

    const parsed =
      parseHtml(html);

    const sourceItems =
      parsed.querySelector(
        'cart-drawer-items'
      );

    const targetItems =
      document.querySelector(
        'cart-drawer-items'
      );

    if (
      sourceItems &&
      targetItems
    ) {
      targetItems.innerHTML =
        sourceItems.innerHTML;
    }

    const sourceFooter =
      parsed.querySelector(
        'cart-drawer .drawer__footer'
      ) ||
      parsed.querySelector(
        '.drawer__footer'
      );

    const targetFooter =
      document.querySelector(
        'cart-drawer .drawer__footer'
      );

    if (
      sourceFooter &&
      targetFooter
    ) {
      targetFooter.innerHTML =
        sourceFooter.innerHTML;
    }

    drawer.classList.toggle(
      'is-empty',
      !parsed.querySelector(
        'cart-drawer:not(.is-empty)'
      )
    );
  }

  async function refreshMainItemsSurface() {
    const targetRoot =
      document.getElementById(
        'main-cart-items'
      );

    const target =
      document.querySelector(
        'cart-items'
      );

    if (
      !targetRoot ||
      !target
    ) {
      return;
    }

    const sectionId =
      targetRoot.dataset.id;

    if (!sectionId) {
      return;
    }

    const html =
      await fetchSectionHtml(
        sectionId
      );

    const parsed =
      parseHtml(html);

    const sourceRoot =
      parsed.getElementById(
        'main-cart-items'
      );

    const source =
      parsed.querySelector(
        'cart-items'
      );

    if (
      target &&
      source
    ) {
      target.innerHTML =
        source.innerHTML;

      target.classList.toggle(
        'is-empty',
        source.classList.contains(
          'is-empty'
        )
      );
    }

    if (
      sourceRoot &&
      targetRoot
    ) {
      targetRoot.classList.toggle(
        'is-empty',
        sourceRoot.classList.contains(
          'is-empty'
        )
      );
    }
  }

  async function refreshMainFooterSurface() {
    const targetRoot =
      document.getElementById(
        'main-cart-footer'
      );

    const target =
      document.querySelector(
        '#main-cart-footer .tsh-cart-summary__refresh'
      );

    if (
      !targetRoot ||
      !target
    ) {
      return;
    }

    const sectionId =
      targetRoot.dataset.id;

    if (!sectionId) {
      return;
    }

    const html =
      await fetchSectionHtml(
        sectionId
      );

    const parsed =
      parseHtml(html);

    const sourceRoot =
      parsed.getElementById(
        'main-cart-footer'
      );

    const source =
      parsed.querySelector(
        '#main-cart-footer .tsh-cart-summary__refresh'
      ) ||
      parsed.querySelector(
        '.tsh-cart-summary__refresh'
      );

    if (
      source &&
      target
    ) {
      target.innerHTML =
        source.innerHTML;
    }

    if (
      sourceRoot &&
      targetRoot
    ) {
      targetRoot.classList.toggle(
        'is-empty',
        sourceRoot.classList.contains(
          'is-empty'
        )
      );
    }
  }

  async function refreshCartIconSurface() {
    const target =
      document.getElementById(
        'cart-icon-bubble'
      );

    if (!target) {
      return;
    }

    const html =
      await fetchSectionHtml(
        'cart-icon-bubble'
      );

    const parsed =
      parseHtml(html);

    const source =
      parsed.getElementById(
        'cart-icon-bubble'
      );

    if (
      target &&
      source
    ) {
      target.innerHTML =
        source.innerHTML;
    }
  }

  async function refreshCartSurfaces() {
    const jobs = [];

    if (
      document.querySelector(
        'cart-drawer'
      )
    ) {
      jobs.push(
        refreshDrawerSurface()
      );
    }

    if (
      document.getElementById(
        'main-cart-items'
      )
    ) {
      jobs.push(
        refreshMainItemsSurface()
      );
    }

    if (
      document.getElementById(
        'main-cart-footer'
      )
    ) {
      jobs.push(
        refreshMainFooterSurface()
      );
    }

    if (
      document.getElementById(
        'cart-icon-bubble'
      )
    ) {
      jobs.push(
        refreshCartIconSurface()
      );
    }

    await Promise.all(jobs);

    window.TSHCartUpsells
      ?.init();
  }

  function publishCartUpdate(
    cart
  ) {
    if (
      typeof publish !==
        'function' ||
      typeof PUB_SUB_EVENTS ===
        'undefined' ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      return;
    }

    publish(
      PUB_SUB_EVENTS.cartUpdate,
      {
        source: SOURCE,
        cartData: cart,
      }
    );
  }

  function setStatus(message) {
    document
      .querySelectorAll(
        '[data-tsh-cart-status]'
      )
      .forEach((node) => {
        node.textContent =
          message || '';
      });
  }

  function setLoading(
    button,
    loading
  ) {
    const section =
      button?.closest(
        '[data-tsh-cart-upsells]'
      );

    if (!section) {
      return;
    }

    section.classList.toggle(
      'is-loading',
      loading
    );

    section.setAttribute(
      'aria-busy',
      loading
        ? 'true'
        : 'false'
    );

    section
      .querySelectorAll(
        '[data-tsh-cart-offer]'
      )
      .forEach(
        (offerButton) => {
          offerButton.disabled =
            loading;
        }
      );

    if (button) {
      if (loading) {
        button.dataset.originalText =
          button.textContent;

        button.textContent =
          'Updating Cart...';
      } else if (
        button.dataset
          .originalText
      ) {
        button.textContent =
          button.dataset
            .originalText;

        delete button.dataset
          .originalText;
      }
    }
  }

  /* =========================================================
     UPSELL UNDO HISTORY
     ========================================================= */

  function getUndoHistory() {
    try {
      const raw =
        sessionStorage.getItem(
          STORAGE_KEY
        );

      if (!raw) {
        return [];
      }

      const parsed =
        JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        sessionStorage.removeItem(
          STORAGE_KEY
        );

        return [];
      }

      const now = Date.now();

      const history =
        parsed.filter(
          (payload) =>
            payload &&
            payload.timestamp &&
            now -
              payload.timestamp <=
              UNDO_LIFETIME
        );

      if (
        history.length !==
        parsed.length
      ) {
        setUndoHistory(
          history
        );
      }

      return history;
    } catch (error) {
      return [];
    }
  }

  function setUndoHistory(
    history
  ) {
    try {
      if (
        !history ||
        !history.length
      ) {
        sessionStorage.removeItem(
          STORAGE_KEY
        );

        return;
      }

      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          history.slice(
            -MAX_UNDO_STEPS
          )
        )
      );
    } catch (error) {
      console.warn(
        'Unable to store cart undo history.',
        error
      );
    }
  }

  function pushUndo(payload) {
    const history =
      getUndoHistory();

    history.push({
      ...payload,
      timestamp: Date.now(),
    });

    setUndoHistory(
      history
    );
  }

  function getUndo() {
    const history =
      getUndoHistory();

    if (!history.length) {
      return null;
    }

    return history[
      history.length - 1
    ];
  }

  function popUndo() {
    const history =
      getUndoHistory();

    if (!history.length) {
      return null;
    }

    const removed =
      history.pop();

    setUndoHistory(
      history
    );

    return removed;
  }

  function clearUndo() {
    try {
      sessionStorage.removeItem(
        STORAGE_KEY
      );
    } catch (error) {
      // No-op.
    }

    document
      .querySelectorAll(
        '[data-tsh-cart-undo]'
      )
      .forEach((node) => {
        node.hidden = true;
        node.innerHTML = '';
      });
  }

  function renderUndo() {
    const payload =
      getUndo();

    document
      .querySelectorAll(
        '[data-tsh-cart-undo]'
      )
      .forEach((node) => {
        if (!payload) {
          node.hidden = true;
          node.innerHTML = '';
          return;
        }

        node.hidden = false;

        node.innerHTML = `
          <span>${escapeHtml(
            payload.message ||
            'Cart updated.'
          )}</span>
          <button
            type="button"
            data-tsh-cart-undo-button
          >
            Undo
          </button>
        `;
      });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(
        /&/g,
        '&amp;'
      )
      .replace(
        /</g,
        '&lt;'
      )
      .replace(
        />/g,
        '&gt;'
      )
      .replace(
        /"/g,
        '&quot;'
      )
      .replace(
        /'/g,
        '&#039;'
      );
  }

  async function changeLine(
    key,
    quantity,
    includeSections = false
  ) {
    const body = {
      id: key,
      quantity,
    };

    let sectionConfig = [];

    if (includeSections) {
      const payload =
        mutationSectionPayload();

      sectionConfig =
        payload.config;

      body.sections =
        payload.sections;

      body.sections_url =
        payload.sections_url;
    }

    const state =
      await requestJson(
        route(
          'cart_change_url',
          '/cart/change.js'
        ),
        body
      );

    if (
      includeSections &&
      state.sections
    ) {
      applySections(
        state.sections,
        sectionConfig
      );
    }

    return state;
  }

  async function updateLines(
    updates,
    includeSections = false
  ) {
    const body = {
      updates,
    };

    let sectionConfig = [];

    if (includeSections) {
      const payload =
        mutationSectionPayload();

      sectionConfig =
        payload.config;

      body.sections =
        payload.sections;

      body.sections_url =
        payload.sections_url;
    }

    const state =
      await requestJson(
        route(
          'cart_update_url',
          '/cart/update.js'
        ),
        body
      );

    if (
      includeSections &&
      state.sections
    ) {
      applySections(
        state.sections,
        sectionConfig
      );
    }

    return state;
  }

  async function addItems(items) {
    return requestJson(
      route(
        'cart_add_url',
        '/cart/add.js'
      ),
      {
        items,
      }
    );
  }

  async function ensureSnapshotLines(
    snapshots
  ) {
    let cart =
      await getCart();

    const updates = {};
    const additions = [];

    snapshots.forEach(
      (snapshot) => {
        const existing =
          topLevelItems(cart).find(
            (item) =>
              sameSnapshotLine(
                item,
                snapshot
              )
          );

        if (existing) {
          if (
            Number(
              existing.quantity
            ) <
            Number(
              snapshot.beforeQty
            )
          ) {
            updates[
              existing.key
            ] =
              Number(
                snapshot.beforeQty
              );
          }

          return;
        }

        const item = {
          id: Number(
            snapshot.variantId
          ),
          quantity: Number(
            snapshot.beforeQty
          ),
        };

        if (
          snapshot.properties &&
          Object.keys(
            snapshot.properties
          ).length
        ) {
          item.properties =
            snapshot.properties;
        }

        if (
          snapshot.sellingPlanId
        ) {
          item.selling_plan =
            snapshot.sellingPlanId;
        }

        additions.push(item);
      }
    );

    if (additions.length) {
      await addItems(
        additions
      );

      cart =
        await getCart();
    }

    if (
      Object.keys(updates)
        .length
    ) {
      await updateLines(
        updates,
        false
      );
    }
  }

  async function rollbackAddedTarget(
    targetHandle,
    snapshots
  ) {
    try {
      let cart =
        await getCart();

      const target =
        findTopLevelByHandle(
          cart,
          targetHandle
        );

      if (target) {
        await changeLine(
          target.key,
          Math.max(
            0,
            Number(
              target.quantity
            ) - 1
          ),
          false
        );
      }

      await ensureSnapshotLines(
        snapshots
      );
    } catch (
      rollbackError
    ) {
      console.error(
        'Unable to fully roll back cart change.',
        rollbackError
      );
    }
  }

  async function performMbQuantity(
    button
  ) {
    const targetQty =
      Number(
        button.dataset
          .targetQty || 0
      );

    const targetHandle =
      button.dataset
        .targetHandle;

    if (
      !targetQty ||
      !targetHandle
    ) {
      throw new Error(
        'This offer is not available right now.'
      );
    }

    const cart =
      await getCart();

    const lines =
      topLevelItems(cart)
        .filter(
          (item) =>
            itemHandle(item) ===
            targetHandle
        );

    const line =
      lines[0] || null;

    const currentTotal =
      lines.reduce(
        (sum, item) =>
          sum +
          Number(
            item.quantity || 0
          ),
        0
      );

    const delta =
      targetQty -
      currentTotal;

    if (
      !line ||
      delta <= 0
    ) {
      throw new Error(
        'Midnight Bloom is no longer eligible for this offer.'
      );
    }

    const undo = {
      type: 'quantity',
      handle:
        targetHandle,
      variantId: Number(
        line.variant_id
      ),
      lineVariantId:
        Number(
          line.variant_id
        ),
      lineProperties:
        normalizeProperties(
          line.properties
        ),
      linePropertiesSignature:
        JSON.stringify(
          normalizeProperties(
            line.properties
          )
        ),
      lineSellingPlanId:
        sellingPlanId(line),
      fromLineQty:
        Number(
          line.quantity
        ),
      message:
        button.dataset.success ||
        'Midnight Bloom added.',
    };

    const state =
      await changeLine(
        line.key,
        Number(
          line.quantity
        ) + delta,
        true
      );

    pushUndo(undo);

    renderUndo();

    publishCartUpdate(
      state
    );
  }

  async function performSwap(
    button
  ) {
    const targetVariant =
      Number(
        button.dataset
          .targetVariant || 0
      );

    const targetHandle =
      button.dataset
        .targetHandle;

    const consumeHandles =
      String(
        button.dataset
          .consumeHandles || ''
      )
        .split(',')
        .map(
          (value) =>
            value.trim()
        )
        .filter(Boolean);

    if (
      !targetVariant ||
      !targetHandle ||
      !consumeHandles.length
    ) {
      throw new Error(
        'This offer is not available right now.'
      );
    }

    const beforeCart =
      await getCart();

    const snapshots = [];

    consumeHandles.forEach(
      (handle) => {
        const line =
          findTopLevelByHandle(
            beforeCart,
            handle
          );

        if (
          !line ||
          Number(
            line.quantity
          ) < 1
        ) {
          throw new Error(
            'Your cart changed. Please try the offer again.'
          );
        }

        snapshots.push(
          snapshotLine(
            line,
            1
          )
        );
      }
    );

    try {
      await addItems([
        {
          id: targetVariant,
          quantity: 1,
        },
      ]);

      const afterAddCart =
        await getCart();

      const updates = {};

      consumeHandles.forEach(
        (handle) => {
          const line =
            findTopLevelByHandle(
              afterAddCart,
              handle
            );

          if (
            !line ||
            Number(
              line.quantity
            ) < 1
          ) {
            throw new Error(
              'Your cart changed before the update could finish.'
            );
          }

          updates[line.key] =
            Math.max(
              0,
              Number(
                line.quantity
              ) - 1
            );
        }
      );

      const state =
        await updateLines(
          updates,
          true
        );

      pushUndo({
        type: 'swap',
        targetHandle,
        targetVariant,
        consumed:
          snapshots,
        message:
          button.dataset.success ||
          'Cart updated.',
      });

      renderUndo();

      publishCartUpdate(
        state
      );
    } catch (error) {
      await rollbackAddedTarget(
        targetHandle,
        snapshots
      );

      await refreshCartSurfaces()
        .catch(() => {});

      throw error;
    }
  }

  async function undoLastChange(
    button
  ) {
    const payload =
      getUndo();

    if (!payload) {
      return;
    }

    button.disabled = true;
    button.textContent =
      'Undoing...';

    setStatus(
      'Restoring your cart...'
    );

    try {
      if (
        payload.type ===
        'quantity'
      ) {
        const cart =
          await getCart();

        const line =
          topLevelItems(cart)
            .find(
              (item) => {
                if (
                  itemHandle(
                    item
                  ) !==
                  payload.handle
                ) {
                  return false;
                }

                if (
                  Number(
                    item.variant_id
                  ) !==
                  Number(
                    payload
                      .lineVariantId ||
                    payload.variantId
                  )
                ) {
                  return false;
                }

                if (
                  String(
                    sellingPlanId(
                      item
                    ) || ''
                  ) !==
                  String(
                    payload
                      .lineSellingPlanId ||
                    ''
                  )
                ) {
                  return false;
                }

                return (
                  JSON.stringify(
                    normalizeProperties(
                      item.properties
                    )
                  ) ===
                  String(
                    payload
                      .linePropertiesSignature ||
                    '{}'
                  )
                );
              }
            );

        if (!line) {
          throw new Error(
            'That change can no longer be undone.'
          );
        }

        await changeLine(
          line.key,
          Number(
            payload.fromLineQty
          ),
          false
        );
      } else if (
        payload.type ===
        'swap'
      ) {
        let cart =
          await getCart();

        const target =
          findTopLevelByHandle(
            cart,
            payload.targetHandle
          );

        if (!target) {
          throw new Error(
            'That change can no longer be undone.'
          );
        }

        await changeLine(
          target.key,
          Math.max(
            0,
            Number(
              target.quantity
            ) - 1
          ),
          false
        );

        await ensureSnapshotLines(
          payload.consumed ||
          []
        );
      }

      popUndo();

      await refreshCartSurfaces();

      const restoredCart =
        await getCart();

      publishCartUpdate(
        restoredCart
      );

      renderUndo();

      setStatus(
        'Change undone.'
      );

      setTimeout(() => {
        setStatus('');
      }, 2200);
    } catch (error) {
      console.error(error);

      setStatus(
        error.message ||
        'Unable to undo that change.'
      );

      button.disabled = false;
      button.textContent =
        'Undo';
    }
  }

  async function handleOffer(
    button
  ) {
    if (
      button.disabled ||
      button.closest(
        '.is-loading'
      )
    ) {
      return;
    }

    setStatus('');

    setLoading(
      button,
      true
    );

    try {
      if (
        button.dataset.action ===
        'mb-qty'
      ) {
        await performMbQuantity(
          button
        );
      } else if (
        button.dataset.action ===
        'swap'
      ) {
        await performSwap(
          button
        );
      } else {
        throw new Error(
          'This offer is not available right now.'
        );
      }
    } catch (error) {
      console.error(error);

      setStatus(
        error.message ||
        'Unable to update your cart. Please try again.'
      );
    } finally {
      setLoading(
        button,
        false
      );
    }
  }

  function bindGlobalEvents() {
    if (
      window
        .__tshCartUpsellsBound
    ) {
      return;
    }

    window
      .__tshCartUpsellsBound =
      true;

    document.addEventListener(
      'click',
      (event) => {
        const offerButton =
          event.target.closest(
            '[data-tsh-cart-offer]'
          );

        if (offerButton) {
          event.preventDefault();

          handleOffer(
            offerButton
          );

          return;
        }

        const undoButton =
          event.target.closest(
            '[data-tsh-cart-undo-button]'
          );

        if (undoButton) {
          event.preventDefault();

          undoLastChange(
            undoButton
          );
        }
      }
    );

    if (
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (
            event?.source !==
            SOURCE
          ) {
            clearUndo();
          }
        }
      );
    }
  }

  function init() {
    bindGlobalEvents();
    renderUndo();
  }

  window.TSHCartUpsells = {
    init,
    clearUndo,
    refresh:
      refreshCartSurfaces,
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    init
  );
})();